import type { QueryKey } from "@tanstack/react-query";

import { EventStatus } from "@sitescene/types";

import type { EventFilters } from "@/api/query-keys";

type InfiniteEventsQueryData = {
	pageParams: unknown[];
	pages: Array<{
		data: Array<{ id: string }>;
		endCursor?: string;
		hasMore?: boolean;
		total?: number;
	}>;
};

/**
 * True when this React Query key is an events list filtered to open alerts
 * (Alerts Dashboard uses `overrideStatus: "open"`).
 */
export const isOpenOnlyEventsListQueryKey = (queryKey: QueryKey): boolean => {
	for (const part of queryKey) {
		if (part == null || typeof part !== "object" || !("filters" in part)) {
			continue;
		}
		const filters = (part as { filters?: EventFilters }).filters;
		return filters?.status === EventStatus.Open;
	}
	return false;
};

/**
 * Strip an event from infinite-query pages and decrement `total` when present.
 * Returns the same reference when the id was not found.
 */
export const removeEventFromInfiniteQueryData = <
	TData extends InfiniteEventsQueryData,
>(
	oldData: TData | undefined,
	eventId: string,
): TData | undefined => {
	if (
		!oldData ||
		typeof oldData !== "object" ||
		!("pages" in oldData) ||
		!Array.isArray(oldData.pages)
	) {
		return oldData;
	}

	let removed = false;
	const withoutEvent = oldData.pages.map((page) => {
		if (!Array.isArray(page.data)) {
			return page;
		}
		const nextData = page.data.filter((event) => {
			if (event.id !== eventId) {
				return true;
			}
			removed = true;
			return false;
		});
		if (nextData.length === page.data.length) {
			return page;
		}
		return {
			...page,
			data: nextData,
		};
	});

	if (!removed) {
		return oldData;
	}

	// Every page response repeats the same `total`, and readers take it from the
	// first one. Decrementing only the page the row happened to sit on left the
	// Dashboard's alert count (and its page count) one too high whenever the
	// resolved alert was on page 2 or later.
	const pages = withoutEvent.map((page) =>
		page.total === undefined
			? page
			: { ...page, total: Math.max(0, page.total - 1) },
	);

	return {
		...oldData,
		pages,
	};
};

/**
 * Real-world case: you resolve an alert, then hit Back to the Dashboard.
 * A websocket `status_changed` must drop that alert from open-only lists —
 * otherwise it reappears (often with the “new event” highlight) even though
 * resolved alerts do not belong on the Dashboard.
 */
export const shouldRemoveEventFromOpenOnlyLists = (options: {
	applyStatusFromIncoming: boolean;
	status: string;
}): boolean =>
	options.applyStatusFromIncoming && options.status !== EventStatus.Open;
